import { Alert, Button, Grid, Input, Layout, Loader, Table, Typography } from "../../../components";
import { Paper } from "../../../components";
import { AlertTitle, Chip, IconButton } from "@mui/material";
import { formatDate, formatDateTime, formatMoney, getBidStatusLabel, getOrderConfirmationColor, getOrderConfirmationLabel, getPaymentStatusColor, getPaymentStatusLabel, nl2br } from "../../../utils";
import { useParams } from "react-router-dom";
import { COMPANY_OPTIONS, TWO_FACTOR_AUTH_OPTIONS } from "../../../constants";
import VisibilityIcon from '@mui/icons-material/Visibility';
import {useAxios} from "../../../hooks";
import { useFormik } from "formik";
import { Save } from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";
import { useContext, useEffect, useRef } from "react";
import * as Yup from 'yup'
import AuthContext from "../../../context/auth";

const statusOptions = [
    'ACCEPTED',
    'DENIED',
    'PENDING',
    'DONE',
    'ON_ORDER',
]

export function Bid () { 
    const {me} = useContext(AuthContext)
    const params = useParams()

    const [bid, refetchBid] = useAxios(`/bid/${params.id}`)
    const [, updateBid] = useAxios({
        url: `/bid/${params.id}/status`,
        method: 'PUT'
    }, {
        manual: true
    })

    const form = useFormik({
        initialValues: {
            status: bid?.data?.status,
        },
        onSubmit: async (values) => {
            const response = await updateBid({
                url: `/bid/${params.id}/status`,
                method: 'PUT',
                data: values
            })

            if (response.status === 204) {
                enqueueSnackbar('Status atualizado com sucesso', {variant: 'success'})
                refetchBid()
            }
        },
        initialStatus: 'draft'
    })

    const twoFAform = useFormik({
        initialValues: {
            two_factor_auth_method: bid?.data?.two_factor_auth_method
        },
        onSubmit: async (values) => {
            const response = await updateBid({
                url: `/bid/${params.id}/mfa`,
                data: values,
                method: 'POST'
            })

            if (response.status === 204) {
                enqueueSnackbar('Dado atualizado com sucesso', {variant: 'success'})
                refetchBid()
            }
        },
        initialStatus: 'draft'
    })

    const commentForm = useFormik({
        initialValues: {
            message: ''
        },
        validationSchema: Yup.object({
            message: Yup.string().required("Este campo é obrigatório")
        }),
        onSubmit: async (values, helpers) => {
            const response = await updateBid({
                url: `/bid/${params.id}/comment`,
                data: {
                    comment: values.message,
                },
                method: 'POST'
            })

            if (response.status === 204) {
                enqueueSnackbar('Comentário enviado com sucesso', {variant: 'success'})
                refetchBid()
                helpers.resetForm()
            }
        }
    })

    const chatContainerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (bid?.data?.bid_comments && chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current?.scrollHeight
        }
    }, [bid?.data?.bid_comments])

    if (!form.values.status && bid?.data?.status && form.status === 'draft') {
        form.resetForm({
            values: {
                status: bid?.data?.status,
            },
            status: 'ready'
        })

        twoFAform.resetForm({
            values: {
                two_factor_auth_method: bid?.data?.two_factor_auth_method
            }
        })
    }

    return (
        <Layout>
            {bid.loading ? (
                <div style={{
                    display: 'flex',
                    flex: '1',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    <Loader />
                </div>
            ) : (
                <Grid container 
                justifyContent="center" 
                gap={2} 
                minHeight={"calc(100vh - 64px)"} 
                alignItems="flex-start" 
                padding={4} 
                direction={{ xs: 'column', sm: 'row', md: 'row' }}
            >
                <Grid item xs={12} sm={7}>
                    <Paper elevation={1}>
                        <Grid container padding={2} spacing={1} justifyContent="center">
                            <Grid item xs={12} marginBottom={1}>
                                <Typography textAlign={"center"} variant="h5">Oferta</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container>
                                    <Grid item xs={6}>
                                        <Typography fontWeight="bold">
                                            ID:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography textAlign="end">
                                            {bid?.data?.id}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container>
                                    <Grid item xs={6}>
                                        <Typography fontWeight="bold">
                                            Data:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography textAlign="end">
                                            {formatDateTime(bid?.data?.created_at)}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container>
                                    <Grid item xs={6}>
                                        <Typography fontWeight="bold">
                                            Fornecedor:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography textAlign="end">
                                            {bid?.data?.user?.name}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container>
                                    <Grid item xs={6}>
                                        <Typography fontWeight="bold">
                                            Titular:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography textAlign="end">
                                            {bid?.data?.account?.holder_name}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container>
                                    <Grid item xs={7} display={"flex"} alignItems={"center"}>
                                        <Typography fontWeight="bold">
                                            Status:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={5} textAlign="right" display={"flex"} alignItems={"center"}>
                                        <Input 
                                            select
                                            value={form.values.status}
                                            options={statusOptions.map(status => ({label: getBidStatusLabel(status) as string, value: status}))}
                                            onChange={e => {
                                                form.setFieldValue('status', e.target.value)
                                            }}
                                            disabled={me?.role !== 'ADMIN'}
                                        />
                                        <IconButton sx={{marginLeft: 1}} disabled={!form.dirty} onClick={form.submitForm}> 
                                            <Save />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container>
                                    <Grid item xs={6}>
                                        <Typography fontWeight="bold">
                                            Motivo:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} textAlign="right">
                                        {bid.data.message}
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container>
                                    <Grid item xs={6}>
                                        <Typography fontWeight="bold">
                                            Companhia:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography textAlign="end">
                                            {COMPANY_OPTIONS.find(company => company.value === bid?.data?.company)?.label}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container>
                                    <Grid item xs={6}>
                                        <Typography fontWeight="bold">
                                            Custo do Milheiro:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography textAlign="end">
                                            {formatMoney(bid?.data?.price_per_k)}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container>
                                    <Grid item xs={6}>
                                        <Typography fontWeight="bold">
                                            Quantidade:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography textAlign="end">
                                            {Number(bid.data?.amount).toLocaleString('pt-BR')}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container>
                                    <Grid item xs={6}>
                                        <Typography fontWeight="bold">
                                            CPFs:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography textAlign="end">
                                            {bid?.data?.pax === null ? '∞' : bid?.data?.pax}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container>
                                    <Grid item xs={7} display={"flex"} alignItems={"center"}>
                                        <Typography fontWeight="bold">
                                            Método de 2FA:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={5} textAlign="right" display={"flex"} alignItems={"center"}>
                                        <Input 
                                            select
                                            value={twoFAform.values.two_factor_auth_method !== null ? twoFAform.values.two_factor_auth_method : 'none'}
                                            options={TWO_FACTOR_AUTH_OPTIONS.map((item: any) => ({
                                                ...item,
                                                value: item.value === null ? 'none' : item.value
                                            }))}
                                            onChange={e => {
                                                twoFAform.setFieldValue('two_factor_auth_method', e.target.value)
                                            }}
                                            disabled={me?.role !== 'ADMIN'}
                                        />
                                        <IconButton sx={{marginLeft: 1}} disabled={!twoFAform.dirty} onClick={twoFAform.submitForm}> 
                                            <Save />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={5}>
                    <Paper elevation={1} >
                        <Grid container padding={2} spacing={1}>
                            <Grid item xs={12} marginBottom={1}>
                                <Typography textAlign={"center"} variant="h5">Emissões</Typography>
                            </Grid>
                            <Grid item xs={12}>
                            <Table 
                                columns={[
                                    { field: 'id', headerName: 'ID', width: 50, type: 'string' },
                                    { field: 'created_at', headerName: 'Data', width: 110, type: 'date', valueGetter: ({value}) => new Date(value), valueFormatter: ({value}) => {
                                        return formatDate(value)
                                    }},
                                    { field: 'miles', headerName: 'Milhas', width: 120, valueFormatter: ({value}) => {
                                        return Number(value).toLocaleString('pt-BR')
                                    }},
                                    { field: 'pax', headerName: 'CPFs', width: 50 },
                                    {
                                        field: 'payment.status', headerName: 'Pagamento', flex: 1, 
                                        valueGetter: ({row}) => {
                                            return getPaymentStatusLabel(row.payment.status)
                                        },
                                        renderCell: ({ formattedValue, row }) => {
                                            return (
                                                <Chip color={getPaymentStatusColor(row.payment.status)} label={formattedValue} />
                                            )
                                        }
                                    },
                                    {
                                        field: 'confirmation', headerName: 'Status', flex: 1, 
                                        valueGetter: ({value}) => {
                                            return getOrderConfirmationLabel(value)
                                        },
                                        renderCell: ({ formattedValue, row }) => {
                                            return (
                                                <Chip color={getOrderConfirmationColor(row)} label={formattedValue} />
                                            )
                                        }
                                    },
                                    {
                                        field: 'actions', headerName: '', sortable: false, filterable: false, disableColumnMenu: true, renderCell: ({row}) => {
                                            return (
                                                <Button variant="outlined" href={`/admin/emissoes/${row.id}`}>
                                                    <VisibilityIcon />
                                                </Button>
                                            )
                                        }
                                    }
                                ]}
                                rows={bid?.data?.orders || []}
                            />
                            </Grid>
                        </Grid>
                    </Paper>
                    <Paper elevation={1} style={{marginTop: 16}}>
                        <Grid container padding={2} spacing={1}>
                            <Grid item xs={12} marginBottom={1}>
                                <Typography textAlign={"center"} variant="h5">Comentários</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <div
                                    ref={chatContainerRef}
                                style={{
                                    maxHeight: 300,
                                    overflowY: 'scroll',
                                    marginBottom: 15
                                    }} 
                                >
                                    {
                                        (bid.data?.bid_comments || []).map((item: any, index: any) => {
                                            return (
                                                <div style={{
                                                    display: 'flex',
                                                    width: '100%',
                                                    justifyContent: me?.id !== item.user.id ? 'left' : 'right'
                                                }}>
                                                    <Alert icon={false} style={{
                                                        width: '80%',
                                                        marginTop: index > 0 ? 10 : undefined
                                                    }} color="info">
                                                        <AlertTitle>
                                                            {formatDateTime(item.created_at)} - {item.user.name}
                                                        </AlertTitle>
                                                        <p dangerouslySetInnerHTML={{
                                                            __html: nl2br(item.comment)
                                                        }} />
                                                    </Alert>
                                                </div>
                                            )
                                        })
                                    }
                                </div>
                                <div style={{display: 'flex', flexDirection: 'row', gap: 5}}>
                                    <Input 
                                        name="message"
                                        label="Comentário"
                                        multiline
                                        maxRows={4}
                                        rows={4}
                                        disabled={commentForm.isSubmitting}
                                        value={commentForm.values.message}
                                        onChange={commentForm.handleChange}
                                        onBlur={commentForm.handleBlur}
                                        helperText={commentForm.touched.message && commentForm.errors.message}
                                        error={!!commentForm.touched.message && !!commentForm.errors.message}
                                    />
                                    
                                    <Button 
                                        disabled={commentForm.isSubmitting || !commentForm.values.message}
                                        onClick={commentForm.submitForm}
                                    > 
                                        Enviar
                                    </Button>
                                </div>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>)}
        </Layout>
    )
}